import { getLocale, translate } from 'shared/lib/i18n.tsx';
import type { TvControlAck } from 'shared/lib/tv-game/types.ts';

import type { BodyPose } from './bodyPoseFrame.ts';
import type { HeadlessMenuOverlay } from './HeadlessMenuOverlay.ts';
import { remoteKey } from './headlessMenu.ts';
import { HeadlessSession, RESULT_GUARD_MS } from './headlessSession.ts';
import type { SkyRaceDisplayEngine } from './SkyRaceDisplayEngine.ts';
import type { SkyRaceMode } from './skyRaceFrame.ts';
import {
  defaultSkyRaceLevels, parseSkyRaceLevels, skyRacePoseFromBody, type SkyRaceLevelOption,
} from './skyRaceHeadless.ts';

export interface HeadlessSkyRaceDeps {
  menu: HeadlessMenuOverlay;
  engine: SkyRaceDisplayEngine;
  getPose(nowMs: number): BodyPose | null;
  /** `tv:start-game` — сервер ответит `room:game-start` (→ `onStarted`). */
  startGame(): Promise<TvControlAck | null>;
  /** `tv:exit-game` — сервер ответит `room:game-cleared` (→ лобби, `stop`). */
  exitGame(): Promise<TvControlAck | null>;
  /** `game:state gameover` телефону: он записывает забег (звёзды, расход энергии). */
  reportGameOver(stars: number, distance: number): void;
}

const MODES: readonly SkyRaceMode[] = ['timed', 'standard'];

/** Список уровней не отвечает дольше — идём со встроенной «Долиной», а не смотрим на пустое небо. */
const LEVELS_TIMEOUT_MS = 5_000;

/**
 * Sky Race в headless-комнате (GEN-18912): уровень и режим кнопками на ТВ, калибровка, автопауза и итог по позе,
 * выход пультом. Страница игры та же, что и с телефоном-контроллером; кадров `SkyRaceData` здесь нет.
 */
export class HeadlessSkyRaceController {
  private readonly session = new HeadlessSession();

  private active = false;

  private raf = 0;

  private levels: SkyRaceLevelOption[] | null = null;

  private level: SkyRaceLevelOption | null = null;

  private mode: SkyRaceMode = 'timed';

  /** Поколение `begin()`: ответ на список уровней от прошлого запуска (после `stop()`) отбрасывается. */
  private gen = 0;

  private abort: AbortController | null = null;

  /** `tv:exit-game` ушёл и ждёт ack: повторы зажатого «Назад» не шлём. */
  private exiting = false;

  constructor(private readonly deps: HeadlessSkyRaceDeps) {
    window.addEventListener('keydown', this.onKeyDown);
  }

  /** Снимает слушатель окна (повторный запуск ТВ-приложения, HMR). */
  dispose(): void {
    this.stop();
    window.removeEventListener('keydown', this.onKeyDown);
  }

  isActive(): boolean {
    return this.active;
  }

  /** Идёт сам забег (после «встаньте в зону» и до итога): скелет на экране в это время скрыт. */
  isGameplay(): boolean {
    const { phase } = this.session;
    return this.active && (phase === 'starting' || phase === 'playing' || phase === 'paused');
  }

  /** Игра выбрана в меню (`room:game-selected`): выбор уровня и режима. Повторный вызов ничего не сбрасывает. */
  begin(): void {
    if (this.active) return;
    this.active = true;
    this.gen += 1;
    const { gen } = this;
    this.session.stop();
    this.deps.engine.beginSetup();
    this.loadLevels().then((levels) => {
      if (gen !== this.gen || !this.active || this.session.phase !== 'idle') return;
      this.levels = levels;
      const [only] = levels;
      if (levels.length === 1 && only) this.pickLevel(only);
      else this.showLevels();
    });
  }

  /** `room:game-start` дошёл — движок уже запустил страницу. */
  onStarted(): void {
    if (this.active) this.session.started();
  }

  onFinished(stars: number, distance: number): void {
    // Поздний или повторный `finished` (после «Ещё раз» или на экране итога) не трогает ни меню, ни калибровку.
    if (!this.active || !this.session.finished()) return;
    this.deps.reportGameOver(stars, distance);
    this.deps.engine.hideStatus();
    this.deps.menu.show({
      key: 'sky-race:result',
      title: translate('headless.result_title'),
      subtitle: translate('headless.result', { stars, meters: translate('game.meters', { n: distance }) }),
      buttons: [
        { id: 'again', label: translate('headless.play_again') },
        { id: 'menu', label: translate('headless.to_menu') },
      ],
      guardMs: RESULT_GUARD_MS,
      overGame: true,
      onPick: (id) => (id === 'again' ? this.calibrate() : this.exit()),
      onBack: () => this.exit(),
    });
  }

  /** Лобби (выход, смена игры, отключение телефона): всё гасим; страницу убирает `engine.reset()`. */
  stop(): void {
    this.active = false;
    this.gen += 1;
    this.abort?.abort();
    this.abort = null;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.session.stop();
    if (this.deps.menu.screenKey?.startsWith('sky-race:')) this.deps.menu.hide();
  }

  private async loadLevels(): Promise<SkyRaceLevelOption[]> {
    const fallbackName = translate('headless.level_default');
    if (this.levels) return this.levels;
    const abort = new AbortController();
    this.abort = abort;
    const timer = window.setTimeout(() => abort.abort(), LEVELS_TIMEOUT_MS);
    try {
      const lang = getLocale() === 'kk' ? 'kz' : getLocale();
      /* Зона public (GEN-19019); старый `/api/sky-race/levels` устарел (GEN-19015). Тело ответа то же. */
      const url = `/api/public/v1/games/sky-race/levels?lang=${encodeURIComponent(lang)}`;
      const res = await fetch(url, { signal: abort.signal });
      return parseSkyRaceLevels(res.ok ? await res.json() : null, fallbackName);
    } catch {
      return defaultSkyRaceLevels(fallbackName);
    } finally {
      window.clearTimeout(timer);
    }
  }

  private showLevels(): void {
    const levels = this.levels ?? [];
    this.deps.menu.show({
      key: 'sky-race:level',
      title: translate('headless.level_title'),
      buttons: levels.map((l) => ({ id: l.slug, label: l.name })),
      focusId: this.level?.slug,
      onPick: (slug) => {
        const level = levels.find((l) => l.slug === slug);
        if (level) this.pickLevel(level);
      },
      onBack: () => this.exit(),
    });
  }

  private pickLevel(level: SkyRaceLevelOption): void {
    this.level = level;
    const modes = MODES.filter((m) => level.modes.includes(m));
    const [onlyMode] = modes;
    if (modes.length === 1 && onlyMode) {
      this.pickMode(onlyMode);
      return;
    }
    this.deps.menu.show({
      key: 'sky-race:mode',
      title: translate('headless.mode_title'),
      subtitle: level.name,
      buttons: modes.map((m) => ({
        id: m,
        label: translate(m === 'timed' ? 'headless.mode_timed' : 'headless.mode_standard'),
      })),
      focusId: this.mode,
      onPick: (mode) => this.pickMode(mode as SkyRaceMode),
      onBack: () => ((this.levels?.length ?? 0) > 1 ? this.showLevels() : this.exit()),
    });
  }

  private pickMode(mode: SkyRaceMode): void {
    this.mode = mode;
    this.calibrate();
  }

  /** Калибровка и забег (первый или «Ещё раз»). */
  private calibrate(): void {
    if (!this.active || !this.level) return;
    this.deps.menu.hide();
    this.deps.engine.startCalibration();
    this.deps.engine.prepareHeadlessPage(this.mode, this.level.slug);
    this.session.beginCalibration(performance.now());
    if (!this.raf) this.raf = requestAnimationFrame(this.tick);
  }

  private exit(): void {
    if (this.exiting) return;
    this.exiting = true;
    this.deps.exitGame()
      .then((ack) => {
        // eslint-disable-next-line no-console
        if (!ack?.ok) console.warn('[TV] tv:exit-game failed', ack);
      })
      .finally(() => {
        this.exiting = false;
      });
  }

  private readonly tick = (now: number): void => {
    this.raf = 0;
    if (!this.active) return;
    const pose = this.deps.getPose(now);
    const action = this.session.tick(now, pose);
    const { engine } = this.deps;
    if (this.session.phase === 'calibration' || this.session.phase === 'starting') {
      engine.setCalibrationProgress(this.session.calibration);
    }
    if (action === 'start') {
      this.deps.startGame().then((ack) => {
        if (ack?.ok) return;
        // eslint-disable-next-line no-console
        console.warn('[TV] tv:start-game failed', ack);
        this.session.startFailed(performance.now());
      });
    } else if (action === 'pause') {
      engine.pause(translate('headless.lost_body'));
    } else if (action === 'resume') {
      engine.resume();
    }
    if (this.session.phase === 'playing') {
      const steering = skyRacePoseFromBody(pose);
      if (steering) engine.setPose(steering);
    }
    // На экране итога считать нечего (`calibrate` запустит цикл заново).
    if (this.session.phase !== 'idle' && this.session.phase !== 'result') this.raf = requestAnimationFrame(this.tick);
  };

  /** «Назад» с пульта во время калибровки и забега (на экранах кнопок его ловит меню). */
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (!this.active || this.deps.menu.isVisible() || remoteKey(e) !== 'back') return;
    e.preventDefault();
    if (e.repeat) return;
    this.exit();
  };
}
